import {
  BotpressIntegrationHub,
  type BotpressIntegrationHubHandle,
  getDeskIntegrationUrl,
  getHiddenIntegrations,
  hasHitlInterface,
  HumanHandoffSection,
  IntegrationNotificationList,
  type IntegrationNotification,
  type IntegrationPatchTransformers,
  useHubHeroBanner,
} from '@bpinternal/integration-hub/runtime'
import { showConfirmationPrompt, showErrorToast, showSuccessToast } from '@bpinternal/ui-kit-next'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useVariant } from '@unleash/proxy-client-react'
import { z } from 'zod'
import { isDeskRedirectEligible } from '~/features/billing/utils/deskRedirect'
import { useFeature } from '~/hooks'
import { getQueryOptions } from '~/services'
import { config } from '~/shared'
import { getApiClient } from '~/stores/clientsStore'
import { useBotEventSubscriptions } from '../hooks/useBotEventSubscriptions'
import type { IntegrationPermissions } from '../types'

const UNINSTALL_CANCELLED_TOKEN = 'integration-hub:uninstall-cancelled'
const HERO_BANNER_FLAG = 'integration-hub-hero-banner'
const EMPTY_BANNER_SLIDES: readonly { title: string; subtitle?: string | null }[] = []
const OAUTH_TEMPLATE_DATA = { env: config.appEnv } as const

const categoryNameToSectionId = (name: string) => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `category-${slug}`
}

const hubStatusSchema = z.object({
  integrations: z
    .record(
      z.string(),
      z.object({
        messages: z
          .array(
            z.object({
              title: z.string().optional(),
              content: z.string().optional(),
              intent: z.enum(['none', 'success', 'warning', 'danger', 'primary']).optional(),
            })
          )
          .optional()
          .default([]),
      })
    )
    .optional()
    .default({}),
})

type HubIntegrationMessages = z.infer<typeof hubStatusSchema>['integrations']

const useHubIntegrationMessages = (): HubIntegrationMessages | null => {
  const variant = useVariant('studio-hub-status')

  return useMemo(() => {
    if (!variant.enabled) {
      return null
    }

    const parsed = hubStatusSchema.safeParse(JSON.parse(variant.payload?.value ?? '{}'))
    return parsed.success ? parsed.data.integrations : null
  }, [variant.enabled, variant.payload?.value])
}

const resolveMessagesForIntegration = (
  integrations: HubIntegrationMessages | null,
  integrationName?: string | null
): IntegrationNotification[] => {
  if (!integrations || !integrationName) {
    return []
  }

  return integrations[integrationName]?.messages ?? []
}

type IntegrationsUrlState = {
  view?: 'hub' | 'installed'
  integration?: string
  category?: string
  q?: string
}

type IntegrationsUrlPatch = Partial<IntegrationsUrlState>

type IntegrationHubPageProps = {
  workspaceId: string
  botId: string
  permissions: IntegrationPermissions
  urlState: IntegrationsUrlState
  onUrlStateChange: (patch: IntegrationsUrlPatch, options?: { replace?: boolean }) => void
}

export const IntegrationHubPage = ({
  workspaceId,
  botId,
  permissions,
  urlState,
  onUrlStateChange,
}: IntegrationHubPageProps) => {
  const client = getApiClient({ workspaceId, botId })
  const hasWriteAccess = permissions.canUpdate || permissions.canDelete
  const disableInstallation = !permissions.canCreate

  const lastOpenedIntegrationTitleRef = useRef<string | null>(null)
  const hubMessages = useHubIntegrationMessages()
  const hubHandleRef = useRef<BotpressIntegrationHubHandle>(null)

  const { enabledEventsByIntegration, handleToggleEvent, isEventToggleDisabled } = useBotEventSubscriptions({
    client,
    workspaceId,
    botId,
    canUpdate: permissions.canUpdate,
    onError: () => showErrorToast('Failed to update event subscription'),
  })

  const { data: workspace } = useSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId }))
  const isDeskRedirectEnabled = useFeature('desk-billing-v4-redirection')
  const deskIntegrationRedirectUrl =
    isDeskRedirectEnabled && isDeskRedirectEligible(workspace) ? getDeskIntegrationUrl() : undefined
  const hideIntegrationNames = useMemo(
    () => getHiddenIntegrations({ showDeskRedirect: Boolean(deskIntegrationRedirectUrl) }),
    [deskIntegrationRedirectUrl]
  )

  const heroBanner = useHubHeroBanner({ flagName: HERO_BANNER_FLAG })

  const { data: bannerIntegration } = useQuery({
    queryKey: ['integration-hub', 'banner', heroBanner?.integrationName],
    queryFn: async () => {
      if (!heroBanner) return null
      const { integrations } = await client.listPublicIntegrations({ name: heroBanner.integrationName, limit: 1 })
      return integrations[0] ?? null
    },
    enabled: !!heroBanner,
    staleTime: 24 * 60 * 60_000,
  })

  const handleBannerClick = useCallback(() => {
    if (!bannerIntegration) return
    hubHandleRef.current?.openIntegrationDetails(bannerIntegration.id)
  }, [bannerIntegration])

  useEffect(() => {
    let cancelled = false
    const storageKey = `integration-hub:nav:${workspaceId}:${botId}`
    let persistedCategory: string | undefined
    try {
      const raw = sessionStorage.getItem(storageKey)
      if (raw) {
        const parsed: unknown = JSON.parse(raw)
        if (parsed && typeof parsed === 'object' && 'selectedCategory' in parsed) {
          const value = (parsed as { selectedCategory?: unknown }).selectedCategory
          if (typeof value === 'string' && value.length > 0) {
            persistedCategory = value
          }
        }
      }
    } catch {
      // ignore storage parse errors
    }

    if (!persistedCategory) {
      return
    }

    const sectionId = categoryNameToSectionId(persistedCategory)
    let remainingAttempts = 40
    const attemptScroll = () => {
      if (cancelled) return
      const section = document.getElementById(sectionId)
      if (section) {
        section.scrollIntoView({ block: 'start', behavior: 'auto' })
        return
      }
      if (remainingAttempts-- > 0) {
        requestAnimationFrame(attemptScroll)
      }
    }
    requestAnimationFrame(attemptScroll)

    return () => {
      cancelled = true
    }
  }, [workspaceId, botId])

  const transformIntegrationPatch = useMemo<IntegrationPatchTransformers>(
    () => ({
      uninstall: async (patch) => {
        const name = lastOpenedIntegrationTitleRef.current ?? 'this integration'
        const confirmed = await showConfirmationPrompt(`Are you sure you want to uninstall ${name}?`, {
          title: 'Uninstall integration',
          confirmLabel: 'Uninstall',
          cancelLabel: 'Cancel',
        })
          .then(() => true)
          .catch(() => false)

        if (!confirmed) {
          throw new Error(UNINSTALL_CANCELLED_TOKEN)
        }
        return patch
      },
    }),
    []
  )

  const renderNotifications = useCallback(
    (ctx: { integration: { title?: string | null; name: string }; definition: { name: string } }) => {
      lastOpenedIntegrationTitleRef.current = ctx.integration.title ?? ctx.integration.name ?? null
      const messages = resolveMessagesForIntegration(hubMessages, ctx.definition.name)
      if (messages.length === 0) {
        return null
      }
      return <IntegrationNotificationList messages={messages} />
    },
    [hubMessages]
  )

  const integrationLabel = (integration: { title?: string | null; name: string }) =>
    integration.title ?? integration.name

  return (
    <div className={'integration-hub flex min-h-0 flex-1 flex-col'}>
      <BotpressIntegrationHub
        ref={hubHandleRef}
        client={client}
        workspaceId={workspaceId}
        botId={botId}
        installEnabled={false}
        initialView={urlState.view ?? 'hub'}
        initialSelectedIntegrationId={urlState.integration ?? null}
        initialSelectedCategory={urlState.category}
        initialSearchInput={urlState.q}
        onViewChange={(view) => onUrlStateChange({ view })}
        onSelectedIntegrationIdChange={(integration) => onUrlStateChange({ integration: integration ?? undefined })}
        onSearchInputChange={(q) => onUrlStateChange({ q: q || undefined }, { replace: true })}
        headerTitle={'Integrations'}
        hasWriteAccess={hasWriteAccess}
        disableInstallation={disableInstallation}
        persistNavigationState
        transformIntegrationPatch={transformIntegrationPatch}
        renderNotifications={renderNotifications}
        onShare={({ integration }) => {
          const url = `${window.location.origin}/workspaces/${workspaceId}/bots/${botId}/integrations?integration=${integration.id}`
          void navigator.clipboard
            .writeText(url)
            .then(() => showSuccessToast('Install link copied to clipboard'))
            .catch(() => showErrorToast('Failed to copy install link'))
        }}
        enabledEventsByIntegration={enabledEventsByIntegration}
        onToggleEvent={handleToggleEvent}
        isEventToggleDisabled={isEventToggleDisabled}
        renderBelowConfigurationForm={({ definition, installedState, workspaceId: wsId, botId: bId }) =>
          installedState && hasHitlInterface(definition) ? (
            <HumanHandoffSection
              client={client}
              workspaceId={wsId}
              botId={bId}
              integrationDef={definition}
              hasWriteAccess={permissions.canUpdate}
              toastHandlers={{ success: showSuccessToast, failure: showErrorToast }}
            />
          ) : null
        }
        bannerSlides={heroBanner?.slides ?? EMPTY_BANNER_SLIDES}
        bannerIconUrl={heroBanner ? bannerIntegration?.iconUrl ?? undefined : undefined}
        onBannerClick={heroBanner ? handleBannerClick : undefined}
        deskIntegrationRedirectUrl={deskIntegrationRedirectUrl}
        hideIntegrationNames={hideIntegrationNames}
        oauthTemplateData={OAUTH_TEMPLATE_DATA}
        onInstall={(integration) => showSuccessToast(`${integrationLabel(integration)} installed`)}
        onUninstall={(integration) => showSuccessToast(`${integrationLabel(integration)} uninstalled`)}
        onEnable={(integration) => showSuccessToast(`${integrationLabel(integration)} enabled`)}
        onDisable={(integration) => showSuccessToast(`${integrationLabel(integration)} disabled`)}
        onUpgrade={(integration) => showSuccessToast(`${integrationLabel(integration)} upgraded`)}
        onError={(error) => {
          if (error.message === UNINSTALL_CANCELLED_TOKEN) {
            return
          }
          showErrorToast(error.message)
        }}
      />
    </div>
  )
}
